import React, { useEffect, useState } from "react";

import {
  MAX_INPUT_LENGTH_ADDRESS,
  MAX_INPUT_LENGTH_CONTACT,
  MAX_INPUT_LENGTH_EORINO,
  MAX_INPUT_LENGTH_GENERIC,
  PartnerModalLabels,
  PortType,
} from "@assets/constants";
import {
  CompanyAddress,
  ErrorTypes,
  ModalProps,
  PickPartnerChangeRegistration,
  Port,
  PostalCodeFormat,
} from "@assets/types";
import { StringUtilities, LabelMapperUtilities, TranslationUtilities } from "@assets/utilities";
import WithFormController from "@components/WithFormController/WithFormController";
import { yupResolver } from "@hookform/resolvers/yup";
import { AsyncSelect, Divider, Form, Grid, Input, Modal } from "@samskip/frontend-components";
import CreateBookingService from "@services/CreateBookingService";
import { FieldError, useForm } from "react-hook-form";
import { Translation } from "react-i18next";
import * as yup from "yup";

interface PartnerRegisterProps extends ModalProps {
  companyAddresses: CompanyAddress[];
  data: PickPartnerChangeRegistration | null;
  onChangeData: (data: PickPartnerChangeRegistration) => void;
  modalTitle: PartnerModalLabels;
  isLoading?: boolean;
}

interface PartnerRegisterForm {
  partner: CompanyAddress | null;
  companyName: string;
  address: string;
  postalCode: string;
  placeCity: Port | null;
  district: string;
  extra: string;
  contact: string;
  email: string;
  phone: string;
  eoriNumber: string;
  vatNumber: string;
}

const defaultFormValues: PartnerRegisterForm = {
  partner: null,
  companyName: "",
  address: "",
  postalCode: "",
  placeCity: null,
  contact: "",
  email: "",
  phone: "",
  district: "",
  extra: "",
  eoriNumber: "",
  vatNumber: "",
};

const PartnerRegisterModal: React.FC<PartnerRegisterProps> = (props: PartnerRegisterProps) => {
  const { companyAddresses, data, onChangeData, open, closeModal, modalTitle } = props;

  const [defaultPlaceCities, setDefaultPlaceCities] = useState<Port[]>([]);
  const [postalCodeFormat, setPostalCodeFormat] = useState<PostalCodeFormat | null>(null);

  const dropdownSchema = {
    label: yup.string(),
    value: yup.mixed(),
  };

  const schema = yup.object({
    partner: yup.lazy(() => {
      if (modalTitle === PartnerModalLabels.PICK) {
        return yup
          .object()
          .shape(dropdownSchema)
          .required(ErrorTypes.RequiredField)
          .typeError(ErrorTypes.RequiredField);
      }
      return yup.mixed().optional();
    }),
    companyName: yup.string().required(ErrorTypes.RequiredField),
    eoriNumber: yup.string().max(MAX_INPUT_LENGTH_EORINO, ErrorTypes.MaxLengthExceeded),
    vatNumber: yup.string().max(MAX_INPUT_LENGTH_GENERIC, ErrorTypes.MaxLengthExceeded),
    address: yup
      .string()
      .required(ErrorTypes.RequiredField)
      .max(MAX_INPUT_LENGTH_ADDRESS, ErrorTypes.MaxLengthExceeded),
    postalCode: yup.lazy(() => {
      if (postalCodeFormat) {
        return yup
          .string()
          .matches(new RegExp(`^${postalCodeFormat.PostalCodeRegexFormat}$`), {
            message: ErrorTypes.PostalCodeError,
            excludeEmptyString: true,
          })
          .required(ErrorTypes.RequiredField);
      }
      return yup.string().required(ErrorTypes.RequiredField);
    }),
    placeCity: yup.object().required(ErrorTypes.RequiredField).typeError(ErrorTypes.RequiredField),
    contact: yup
      .string()
      .required(ErrorTypes.RequiredField)
      .max(MAX_INPUT_LENGTH_CONTACT, ErrorTypes.MaxLengthExceeded),
    email: yup
      .string()
      .required(ErrorTypes.RequiredField)
      .test({
        name: "invalidEmails",
        exclusive: false,
        params: {},
        message: ErrorTypes.EmailIsNotValid,
        test: function (value: string) {
          return StringUtilities.validEmails(value);
        },
      }),
    phone: yup.string().max(MAX_INPUT_LENGTH_GENERIC, ErrorTypes.MaxLengthExceeded),
  });

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    reset,
    getValues,
  } = useForm({
    defaultValues: defaultFormValues,
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  useEffect(() => {
    CreateBookingService.getPorts(PortType.SEA).then((localPorts: Port[]) => {
      setDefaultPlaceCities(localPorts);
    });
  }, []);

  useEffect(() => {
    setValue("partner", data?.partner ? data.partner : null);
    setValue("companyName", data?.companyName ?? "");
    setValue("address", data?.address ?? "");
    setValue("postalCode", data?.postalCode ?? "");
    setValue("placeCity", data?.placeCity ?? null);
    setValue("contact", data?.contact ?? "");
    setValue("email", data?.email ?? "");
    setValue("phone", data?.phone ?? "");
    setValue("district", data?.district ?? "");
    setValue("extra", data?.extra ?? "");
    setValue("eoriNumber", data?.eoriNumber ?? "");
    setValue("vatNumber", data?.vatNumber ?? "");
  }, [data]);

  const onClose = () => {
    closeModal();
    if (modalTitle === PartnerModalLabels.PICK) reset();
  };

  const onChange = (option: CompanyAddress) => {
    setValue("partner", option ? option : null);
    setValue("companyName", option?.FullName ?? "");
    setValue(
      "address",
      `${
        option?.Address2 !== "" && option?.Address2 !== null ? option?.Address2 : option?.Address3
      }`
    );
    setValue("postalCode", option?.PostCode ?? "");
    setValue("placeCity", {
      PointCode: option?.PointCode,
      FullName: option?.City,
      Country: option?.CountryName,
      CountryCode: option?.CountryCode,
      PortCode: option?.PostCode,
      PortType: "W",
      TimeZone: "",
      TimeZoneDiff: 0,
      LocalName: "",
    });
    setValue("contact", option?.ContactName ?? "");
    setValue("email", option?.ContactEmail ?? "");
    setValue("phone", option?.ContactNumber ?? "");
    setValue("district", "");
    setValue("extra", "");
    setValue("eoriNumber", option?.EORINumber ?? "");
    setValue("vatNumber", option?.VATNumber ?? "");

    if (option && option.CountryCode) {
      CreateBookingService.getPostalCodeFormatByCountry(option.CountryCode).then(
        (pc: PostalCodeFormat) => {
          setPostalCodeFormat(pc);
        }
      );
    }
  };

  const searchPort = (searchString: string): Promise<Port[]> => {
    return new Promise((resolve) => {
      CreateBookingService.searchPartnerPorts({
        searchString: searchString,
        size: 10,
      }).then((ports: Port[]) => {
        resolve(ports);
      });
    });
  };

  const searchPartner = (searchString: string): Promise<CompanyAddress[]> => {
    return new Promise((resolve) => {
      CreateBookingService.searchPartners(searchString).then((ports: CompanyAddress[]) => {
        resolve(ports);
      });
    });
  };

  const onSubmit = (formData: PartnerRegisterForm) => {
    const newData = {
      partner: formData.partner ?? null,
      companyName: formData.companyName ?? "",
      address: formData.address ?? "",
      addressCode: formData.partner?.AddressCode ?? "",
      postalCode: formData.postalCode ?? "",
      placeCity: formData.placeCity ?? null,
      contact: formData.contact ?? "",
      email: formData.email ?? "",
      phone: formData.phone ?? "",
      district: formData.district ?? "",
      extra: formData.extra ?? "",
      eoriNumber: formData.eoriNumber ?? "",
      vatNumber: formData.vatNumber ?? "",
    };
    onChangeData(newData);
    closeModal();
    return;
  };

  const onChangePortCity = (port: Port) => {
    if (port && port.CountryCode) {
      CreateBookingService.getPostalCodeFormatByCountry(port.CountryCode).then(
        (pc: PostalCodeFormat) => {
          setPostalCodeFormat(pc);
        }
      );
    }
  };

  return (
    <Translation>
      {(t) => {
        return (
          <Modal
            closeIcon
            closeOnOutsideClick={props.closeOnOutsideClick}
            divided="footer"
            onClose={onClose}
            open={open}
            primaryButtonProps={{
              form: "partner_form",
              children: t("LABEL_SAVE"),
              type: "submit",
            }}
            secondaryButtonProps={{ children: t("LABEL_CANCEL"), onClick: onClose }}
            title={t(modalTitle)}
          >
            <Form gap="small" id="partner_form" method="post" onSubmit={handleSubmit(onSubmit)}>
              {modalTitle === PartnerModalLabels.PICK && (
                <WithFormController control={control} name="partner">
                  <AsyncSelect
                    defaultOptions={companyAddresses}
                    errorMessage={TranslationUtilities.getErrorMessage("partner", errors)}
                    fullWidth
                    getOptionLabel={LabelMapperUtilities.partnerLabelMapper}
                    getOptionValue={LabelMapperUtilities.partnerValueMapper}
                    hasError={errors.partner != null}
                    id="partner"
                    isLoading={props.isLoading}
                    label={t("LABEL_PARTNER")}
                    labelCase="none"
                    loadOptions={searchPartner}
                    onChange={onChange}
                    placeholder={t("TEXT_ENTER_PARTNER")}
                  />
                </WithFormController>
              )}
              <Input
                fullWidth
                label={t("LABEL_COMPANY_NAME")}
                labelCase="none"
                type="text"
                {...register("companyName")}
                errorMessage={TranslationUtilities.getErrorMessage("companyName", errors)}
                hasError={errors.companyName != null}
                id="companyName"
                placeholder={t("TEXT_ENTER_COMPANY_NAME")}
              />
              <Grid mdCol={2}>
                <Input
                  fullWidth
                  label={t("LABEL_EORI_NUMBER")}
                  labelCase="none"
                  type="text"
                  {...register("eoriNumber")}
                  disabled={!!getValues("partner.EORINumber")}
                  errorMessage={TranslationUtilities.getErrorMessage("eoriNumber", errors, {
                    n: MAX_INPUT_LENGTH_EORINO,
                  })}
                  hasError={errors.eoriNumber != null}
                  id="eoriNumber"
                  infoMessage={t("INFO_EORI_NUMBER")}
                  optional
                  placeholder={t("TEXT_ENTER_EORI_NUMBER")}
                />
                <Input
                  fullWidth
                  label={t("LABEL_VAT_NUMBER")}
                  labelCase="none"
                  type="text"
                  {...register("vatNumber")}
                  disabled={!!getValues("partner.VATNumber")}
                  errorMessage={TranslationUtilities.getErrorMessage("vatNumber", errors, {
                    n: MAX_INPUT_LENGTH_GENERIC,
                  })}
                  hasError={errors.vatNumber != null}
                  id="vatNumber"
                  infoMessage={t("INFO_VAT_NUMBER")}
                  optional
                  placeholder={t("TEXT_ENTER_VAT_NUMBER")}
                />
              </Grid>
              <Input
                fullWidth
                label={t("LABEL_ADDRESS")}
                labelCase="none"
                type="text"
                {...register("address")}
                errorMessage={TranslationUtilities.getErrorMessage("address", errors, {
                  n: MAX_INPUT_LENGTH_ADDRESS,
                })}
                hasError={errors.address != null}
                id="address"
                placeholder={t("TEXT_ENTERADDRESS")}
              />
              <Grid mdCol={2}>
                <WithFormController control={control} name="placeCity">
                  <AsyncSelect
                    defaultOptions={defaultPlaceCities}
                    errorMessage={t((errors.placeCity as FieldError)?.message || "")}
                    fullWidth
                    getOptionLabel={LabelMapperUtilities.portLabelMapper}
                    getOptionValue={LabelMapperUtilities.portValueMapper}
                    hasError={errors.placeCity != null}
                    id="placeCity"
                    label={t("LABEL_CITY_COUNTRY")}
                    labelCase="none"
                    loadOptions={searchPort}
                    onChange={onChangePortCity}
                    placeholder={t("TEXT_ENTER_CITY")}
                  />
                </WithFormController>
                <Input
                  fullWidth
                  label={t("LABEL_POSTALCODE")}
                  labelCase="none"
                  type="text"
                  {...register("postalCode")}
                  errorMessage={TranslationUtilities.getErrorMessage("postalCode", errors)}
                  hasError={errors.postalCode != null}
                  helperDescription={
                    postalCodeFormat && postalCodeFormat?.PostalCodeExample
                      ? t("TEXT_EXAMPLE") + ` ${postalCodeFormat?.PostalCodeExample}`
                      : undefined
                  }
                  id="postalcode"
                  placeholder={t("TEXT_ENTERPOSTCODE")}
                />
              </Grid>
              <Input
                fullWidth
                label={t("LABEL_DISTRICT")}
                labelCase="none"
                type="text"
                {...register("district")}
                id="district"
                optional
                placeholder={t("TEXT_ENTERDISTRICT")}
              />
              <Input
                fullWidth
                label={t("LABEL_EXTRA")}
                labelCase="none"
                type="text"
                {...register("extra")}
                id="extra"
                optional
                placeholder={t("TEXT_ENTEREXTRA")}
              />
              <Divider />
              <Input
                fullWidth
                label={t("LABEL_CONTACT_PERSON_NAME")}
                labelCase="none"
                type="text"
                {...register("contact")}
                errorMessage={TranslationUtilities.getErrorMessage("contact", errors, {
                  n: MAX_INPUT_LENGTH_CONTACT,
                })}
                hasError={errors.contact != null}
                id="contact"
                placeholder={t("TEXT_ENTER_CONTACT_PERSON_NAME")}
              />
              <Grid mdCol={2}>
                <Input
                  fullWidth
                  inputmode="email"
                  label={t("LABEL_EMAIL")}
                  labelCase="none"
                  type="text"
                  {...register("email")}
                  errorMessage={TranslationUtilities.getErrorMessage("email", errors)}
                  hasError={errors.email != null}
                  id="email"
                  placeholder={t("TEXT_ENTEREMAIL")}
                />
                <Input
                  fullWidth
                  label={t("LABEL_PHONE")}
                  labelCase="none"
                  type="text"
                  {...register("phone")}
                  errorMessage={TranslationUtilities.getErrorMessage("phone", errors, {
                    n: MAX_INPUT_LENGTH_GENERIC,
                  })}
                  hasError={errors.phone != null}
                  id="phone"
                  placeholder={t("TEXT_ENTERPHONE")}
                />
              </Grid>
            </Form>
          </Modal>
        );
      }}
    </Translation>
  );
};

export default PartnerRegisterModal;
